import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { CheckIcon } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * shadcn radix-nova Checkbox primitive.
 *
 * Thin wrapper over `@radix-ui/react-checkbox` that renders a `CheckIcon`
 * indicator inside a button-role element. Carry the `peer` class so the
 * Label's `peer-disabled:` flow dims the surrounding label text when the
 * checkbox becomes disabled.
 *
 * Consumer note: Radix Checkbox exposes `checked` + `onCheckedChange` rather
 * than the native HTML `onChange(e).checked` shape — update call sites
 * accordingly (see `routes/signup.tsx`).
 *
 * For form submission integration a hidden `<input type="hidden">` sibling is
 * required (Radix Checkbox does not auto-generate one). None of the current
 * routes submit checkbox state; add the sibling when that changes.
 */
const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    data-slot="checkbox"
    className={cn(
      "peer size-4 shrink-0 rounded-sm border border-input bg-transparent transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary dark:bg-input/30",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      data-slot="checkbox-indicator"
      className="flex items-center justify-center text-current transition-none"
    >
      <CheckIcon className="size-3.5" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = "Checkbox"

export { Checkbox }
